import { buildApiUrl } from './apiBase';

const WS_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_WS_DEBUG === 'true';
const RECONNECT_DELAY_MS = 2000;
const CONNECT_TIMEOUT_MS = 10000;

const debugLog = (...args) => {
  if (WS_DEBUG) {
    console.debug(...args);
  }
};

const buildRealtimeUrl = () => {
  const apiUrl = new URL(buildApiUrl('/api/ws/realtime'), window.location.origin);
  apiUrl.protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
  return apiUrl.toString();
};

class TradoProRealtimeWebSocket {
  constructor() {
    this.socket = null;
    this.connectPromise = null;
    this.disconnectRequested = false;
    this.reconnectTimer = null;
    this.token = null;
    this.accountId = null;
    this.subscribedContracts = new Set();
    this.subscribers = {
      quote: [],
      trade: [],
      depth: [],
      position: [],
      order: [],
      account: [],
    };
  }

  setToken(token) {
    this.token = String(token || '').trim() || null;
  }

  setAccountId(accountId) {
    // Retained for the existing dashboard API. Account state is refreshed by
    // backend polling/reconciliation and is not subscribed from the broker in
    // the public browser.
    this.accountId = accountId;
  }

  subscribe(event, callback) {
    if (!this.subscribers[event]) {
      return () => {};
    }
    this.subscribers[event].push(callback);
    return () => {
      this.subscribers[event] = this.subscribers[event].filter((entry) => entry !== callback);
    };
  }

  emit(event, data) {
    (this.subscribers[event] || []).forEach((callback) => callback(data));
  }

  send(payload) {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      return false;
    }
    this.socket.send(JSON.stringify(payload));
    return true;
  }

  sendCurrentSubscriptions() {
    if (this.subscribedContracts.size === 0) {
      return;
    }
    this.send({
      type: 'subscribe',
      contract_ids: Array.from(this.subscribedContracts),
    });
  }

  handleMessage(rawEvent) {
    let message;
    try {
      message = JSON.parse(rawEvent.data);
    } catch (_error) {
      return;
    }

    if (message?.type !== 'quote') {
      if (this.subscribers[message?.type]) {
        this.emit(message.type, message.data ?? message);
      }
      return;
    }

    const quote = {
      contractId: message.contractId,
      symbol: message.symbol,
      ...(message.data || {}),
    };
    this.emit('quote', quote);
    this.emit('depth', {
      contractId: message.contractId,
      high: quote.high ?? quote.dayHigh ?? 0,
      low: quote.low ?? quote.dayLow ?? 0,
      volume: quote.volume ?? 0,
      book: { bids: [], asks: [] },
      raw: quote,
    });
  }

  scheduleReconnect() {
    if (this.disconnectRequested || this.reconnectTimer) {
      return;
    }
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, RECONNECT_DELAY_MS);
  }

  async connect() {
    if (this.socket?.readyState === WebSocket.OPEN) {
      return true;
    }
    if (this.connectPromise) {
      return this.connectPromise;
    }
    if (!this.token) {
      console.error('No TradoPro session token available for realtime connection');
      return false;
    }

    this.disconnectRequested = false;
    const socket = new WebSocket(
      buildRealtimeUrl(),
      ['tradopro-v1', `bearer.${this.token}`],
    );
    this.socket = socket;

    this.connectPromise = new Promise((resolve) => {
      let settled = false;
      const finish = (connected) => {
        if (!settled) {
          settled = true;
          resolve(connected);
        }
      };
      const timeout = window.setTimeout(() => {
        if (socket.readyState !== WebSocket.OPEN) {
          socket.close();
          finish(false);
        }
      }, CONNECT_TIMEOUT_MS);

      socket.onopen = () => {
        window.clearTimeout(timeout);
        debugLog('TradoPro realtime relay connected');
        this.sendCurrentSubscriptions();
        finish(true);
      };
      socket.onmessage = (event) => this.handleMessage(event);
      socket.onerror = () => finish(false);
      socket.onclose = () => {
        window.clearTimeout(timeout);
        if (this.socket === socket) {
          this.socket = null;
        }
        finish(false);
        this.scheduleReconnect();
      };
    });

    try {
      return await this.connectPromise;
    } finally {
      this.connectPromise = null;
    }
  }

  async subscribeToContract(contractId) {
    if (!contractId) {
      return;
    }
    this.subscribedContracts.add(contractId);
    this.send({ type: 'subscribe', contract_ids: [contractId] });
  }

  async unsubscribeFromContract(contractId) {
    if (!contractId) {
      return;
    }
    this.subscribedContracts.delete(contractId);
    this.send({ type: 'unsubscribe', contract_ids: [contractId] });
  }

  async disconnect() {
    this.disconnectRequested = true;
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const socket = this.socket;
    this.socket = null;
    this.connectPromise = null;
    this.subscribedContracts.clear();
    if (socket && socket.readyState !== WebSocket.CLOSED) {
      socket.close(1000, 'client_disconnect');
    }
  }

  getState() {
    return {
      market: this.socket?.readyState === WebSocket.OPEN ? 'Connected' : 'Disconnected',
      user: 'Polling',
    };
  }

  subscribeToQuotes(contractId, callback) {
    if (contractId) {
      void this.subscribeToContract(contractId);
    }
    return this.subscribe('quote', (data) => {
      if (!contractId || data?.contractId === contractId) {
        callback({
          price: data?.lastPrice ?? data?.price ?? data?.last ?? 0,
          bid: data?.bestBid ?? data?.bid ?? 0,
          ask: data?.bestAsk ?? data?.ask ?? 0,
          high: data?.high ?? data?.dayHigh ?? 0,
          low: data?.low ?? data?.dayLow ?? 0,
          volume: data?.volume ?? 0,
          timestamp: data?.timestamp ?? data?.time ?? data?.tradeTime,
          raw: data,
        });
      }
    });
  }

  subscribeToMarketDepth(contractId, callback) {
    if (contractId) {
      void this.subscribeToContract(contractId);
    }
    return this.subscribe('depth', (data) => {
      if (!contractId || data?.contractId === contractId) {
        callback({
          high: data?.high ?? 0,
          low: data?.low ?? 0,
          volume: data?.volume ?? 0,
          book: data?.book ?? { bids: [], asks: [] },
          raw: data?.raw ?? data,
        });
      }
    });
  }

  async unsubscribe(contractId) {
    return this.unsubscribeFromContract(contractId);
  }
}

const projectXWS = new TradoProRealtimeWebSocket();

export default projectXWS;
